import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Button,
  Paper,
  Collapse,
  Skeleton,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  InputAdornment,
  Badge,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LocalGroceryStoreIcon from "@mui/icons-material/LocalGroceryStore";
import HomeIcon from "@mui/icons-material/Home";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import BuildIcon from "@mui/icons-material/Build";
import MovieIcon from "@mui/icons-material/Movie";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import TuneIcon from "@mui/icons-material/Tune";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CodeIcon from "@mui/icons-material/Code";
import { categoryOptions } from "../../../../Constant";

export const getCategoryMeta = (catValue) => {
  switch (catValue) {
    case "kiraya_bijli_pani":
      return {
        label: "Rent & Bills",
        icon: <HomeIcon sx={{ fontSize: 18 }} />,
        color: "#8b5cf6",
        bg: "rgba(139, 92, 246, 0.14)",
      };
    case "rashan_ghar":
      return {
        label: "Groceries",
        icon: <LocalGroceryStoreIcon sx={{ fontSize: 18 }} />,
        color: "#22c55e",
        bg: "rgba(34, 197, 94, 0.14)",
      };
    case "khana_nashta":
      return {
        label: "Food & Dining",
        icon: <RestaurantIcon sx={{ fontSize: 18 }} />,
        color: "#f97316",
        bg: "rgba(249, 115, 22, 0.14)",
      };
    case "gadi_petrol_safar":
      return {
        label: "Travel & Fuel",
        icon: <DirectionsCarIcon sx={{ fontSize: 18 }} />,
        color: "#0ea5e9",
        bg: "rgba(14, 165, 233, 0.14)",
      };
    case "dawai_ilaaj":
      return {
        label: "Medical",
        icon: <LocalHospitalIcon sx={{ fontSize: 18 }} />,
        color: "#ef4444",
        bg: "rgba(239, 68, 68, 0.14)",
      };
    case "recharge_subscription":
      return {
        label: "Recharge & Net",
        icon: <PhoneAndroidIcon sx={{ fontSize: 18 }} />,
        color: "#06b6d4",
        bg: "rgba(6, 182, 212, 0.14)",
      };
    case "enjoyment":
      return {
        label: "Entertainment",
        icon: <MovieIcon sx={{ fontSize: 18 }} />,
        color: "#ec4899",
        bg: "rgba(236, 72, 153, 0.14)",
      };
    case "ghar_sudhar":
      return {
        label: "Repairs",
        icon: <BuildIcon sx={{ fontSize: 18 }} />,
        color: "#eab308",
        bg: "rgba(234, 179, 8, 0.14)",
      };
    default:
      return {
        label: "General",
        icon: <ReceiptLongIcon sx={{ fontSize: 18 }} />,
        color: "#64748b",
        bg: "rgba(100, 116, 139, 0.14)",
      };
  }
};

export const formatDateBadge = (dateStr) => {
  if (!dateStr) return "";
  try {
    const [y, m, d] = dateStr.split("-");
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    return date.toLocaleDateString("en-US", { day: "numeric", month: "short" });
  } catch {
    return dateStr;
  }
};

/**
 * HomeMobileView — Optimized, mobile-first feed for Home Expenses.
 * Features instant debounced search, quick preset category pills, filter modal,
 * compact 2-line cards with expandable drawer, bill image previews, and delete actions.
 */
const ExpenseMobileView = ({
  data = [],
  loading = false,
  rowCount = 0,
  filterdTotal = 0,
  searchQuery = "",
  onSearchChange,
  filterModel = { items: [] },
  setFilterModel,
  paginationModel = { page: 0, pageSize: 10 },
  setPaginationModel,
  session,
  onOpenBillModal,
  onDeleteExpense,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [expandedMap, setExpandedMap] = useState({});
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Local draft filters state for advanced modal search
  const [localFilters, setLocalFilters] = useState({
    title: "",
    paid_by: "",
    brand: "",
    expense_detail: "",
    category: "",
    startDate: "",
    endDate: "",
  });

  // Sync draft filters whenever modal opens
  useEffect(() => {
    if (filterModalOpen) {
      const eqDate = filterModel.items.find(
        (i) => i.field === "expense_time" && i.operator === "="
      )?.value;
      const gteDate = filterModel.items.find(
        (i) => i.field === "expense_time" && i.operator === ">="
      )?.value;
      const lteDate = filterModel.items.find(
        (i) => i.field === "expense_time" && i.operator === "<="
      )?.value;

      setLocalFilters({
        title: filterModel.items.find((i) => i.field === "title")?.value ?? "",
        paid_by: filterModel.items.find((i) => i.field === "paid_by")?.value ?? "",
        brand: filterModel.items.find((i) => i.field === "brand")?.value ?? "",
        expense_detail: filterModel.items.find((i) => i.field === "expense_detail")?.value ?? "",
        category: filterModel.items.find((i) => i.field === "category")?.value ?? "",
        startDate: eqDate || gteDate || "",
        endDate: eqDate || lteDate || "",
      });
    }
  }, [filterModalOpen, filterModel]);

  // Selected quick filter
  const currentCategoryFilter =
    filterModel.items.find((item) => item.field === "category")?.value ?? "";

  const toggleExpand = (id) => {
    setExpandedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const isAllExpanded =
    data.length > 0 && data.every((row) => !!expandedMap[row.id]);

  const handleToggleAll = () => {
    if (isAllExpanded) {
      setExpandedMap({});
    } else {
      const newMap = { ...expandedMap };
      data.forEach((row) => {
        newMap[row.id] = true;
      });
      setExpandedMap(newMap);
    }
  };

  // Category Quick Preset Handler
  const handleSelectCategory = (catValue) => {
    setFilterModel((prev) => {
      const newItems = prev.items.filter((i) => i.field !== "category");
      if (catValue && currentCategoryFilter !== catValue) {
        newItems.push({
          field: "category",
          operator: "=",
          value: catValue,
        });
      }
      return { ...prev, items: newItems };
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Remove a single active filter chip
  const handleRemoveSingleFilter = (fieldToRemove) => {
    setFilterModel((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.field !== fieldToRemove),
    }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Set quick date preset in modal
  const handleSetDatePreset = (preset) => {
    const today = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    if (preset === "today") {
      const todayStr = toYMD(today);
      setLocalFilters((prev) => ({
        ...prev,
        startDate: todayStr,
        endDate: todayStr,
      }));
    } else if (preset === "yesterday") {
      const yest = new Date();
      yest.setDate(yest.getDate() - 1);
      const yestStr = toYMD(yest);
      setLocalFilters((prev) => ({
        ...prev,
        startDate: yestStr,
        endDate: yestStr,
      }));
    } else if (preset === "thisMonth") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setLocalFilters((prev) => ({
        ...prev,
        startDate: toYMD(firstDay),
        endDate: toYMD(today),
      }));
    } else if (preset === "clear") {
      setLocalFilters((prev) => ({
        ...prev,
        startDate: "",
        endDate: "",
      }));
    }
  };

  // Apply all column filters from modal
  const handleApplyModalFilters = () => {
    const newItems = [];
    if (localFilters.title.trim()) {
      newItems.push({
        field: "title",
        operator: "contains",
        value: localFilters.title.trim(),
      });
    }
    if (localFilters.paid_by.trim()) {
      newItems.push({
        field: "paid_by",
        operator: "contains",
        value: localFilters.paid_by.trim(),
      });
    }
    if (localFilters.brand.trim()) {
      newItems.push({
        field: "brand",
        operator: "contains",
        value: localFilters.brand.trim(),
      });
    }
    if (localFilters.expense_detail.trim()) {
      newItems.push({
        field: "expense_detail",
        operator: "contains",
        value: localFilters.expense_detail.trim(),
      });
    }
    if (localFilters.category.trim()) {
      newItems.push({
        field: "category",
        operator: "=",
        value: localFilters.category.trim(),
      });
    }

    // Date Range handling:
    // If both dates are same, show only that day (operator = or >= and <=)
    if (localFilters.startDate && localFilters.endDate) {
      if (localFilters.startDate === localFilters.endDate) {
        newItems.push({
          field: "expense_time",
          operator: "=",
          value: localFilters.startDate,
        });
      } else {
        newItems.push({
          field: "expense_time",
          operator: ">=",
          value: localFilters.startDate,
        });
        newItems.push({
          field: "expense_time",
          operator: "<=",
          value: localFilters.endDate,
        });
      }
    } else if (localFilters.startDate) {
      newItems.push({
        field: "expense_time",
        operator: ">=",
        value: localFilters.startDate,
      });
    } else if (localFilters.endDate) {
      newItems.push({
        field: "expense_time",
        operator: "<=",
        value: localFilters.endDate,
      });
    }

    setFilterModel((prev) => ({ ...prev, items: newItems }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setFilterModalOpen(false);
  };

  // Reset local draft in modal
  const handleResetModalFilters = () => {
    setLocalFilters({
      title: "",
      paid_by: "",
      brand: "",
      expense_detail: "",
      category: "",
      startDate: "",
      endDate: "",
    });
  };

  const hasDateFilter = !!(localFilters.startDate || localFilters.endDate);
  const localActiveCount =
    (localFilters.title.trim() ? 1 : 0) +
    (localFilters.paid_by.trim() ? 1 : 0) +
    (localFilters.brand.trim() ? 1 : 0) +
    (localFilters.expense_detail.trim() ? 1 : 0) +
    (localFilters.category.trim() ? 1 : 0) +
    (hasDateFilter ? 1 : 0);

  const dateItemsCount = filterModel.items.filter((i) => i.field === "expense_time").length;
  const nonDateItemsCount = filterModel.items.filter(
    (i) => i.field !== "expense_time" && i.value && i.value.toString().trim() !== ""
  ).length;
  const activeFiltersCount = nonDateItemsCount + (dateItemsCount > 0 ? 1 : 0);

  const handleClearAllFilters = () => {
    setFilterModel((prev) => ({ ...prev, items: [] }));
    setLocalFilters({
      title: "",
      paid_by: "",
      brand: "",
      expense_detail: "",
      category: "",
      startDate: "",
      endDate: "",
    });
    if (onSearchChange) onSearchChange({ target: { value: "" } });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setFilterModalOpen(false);
  };

  return (
    <Box sx={{ width: "100%", pt: 1, pb: 4 }}>
      {/* 1. Search Bar & Filter Trigger */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search title (milk) or query (addedby:jai date:today)..."
          value={searchQuery}
          onChange={onSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onSearchChange({ target: { value: "" } })}
                >
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
              "& fieldset": { borderColor: "divider" },
            },
          }}
        />

        <Badge
          badgeContent={activeFiltersCount}
          color="primary"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 10,
              height: 18,
              minWidth: 18,
              fontWeight: 700,
            },
          }}
        >
          <IconButton
            onClick={() => setFilterModalOpen(true)}
            sx={{
              p: 1.1,
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: activeFiltersCount > 0 ? "primary.main" : "divider",
              bgcolor: activeFiltersCount > 0
                ? isDark
                  ? "rgba(99, 102, 241, 0.18)"
                  : "rgba(79, 70, 229, 0.08)"
                : isDark
                  ? "rgba(255,255,255,0.05)"
                  : "#ffffff",
              color: activeFiltersCount > 0 ? "primary.main" : "text.secondary",
            }}
            title="Filter Expenses"
          >
            <FilterListIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Badge>
      </Box>

      {/* 2. Quick Preset Chips Carousel */}
      <Box
        sx={{
          display: "flex",
          gap: 0.8,
          overflowX: "auto",
          whiteSpace: "nowrap",
          pb: 0.8,
          mb: 0.8,
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        <Chip
          size="small"
          label="All Categories"
          onClick={() => handleSelectCategory("")}
          variant={currentCategoryFilter === "" ? "filled" : "outlined"}
          color={currentCategoryFilter === "" ? "primary" : "default"}
          sx={{ fontWeight: 600, fontSize: 12, height: 28, borderRadius: 2 }}
        />
        {categoryOptions.slice(0, 6).map((cat) => {
          const isSelected = currentCategoryFilter === cat.value;
          const meta = getCategoryMeta(cat.value);
          return (
            <Chip
              key={cat.value}
              size="small"
              icon={React.cloneElement(meta.icon, {
                sx: { color: isSelected ? "inherit" : meta.color },
              })}
              label={meta.label}
              onClick={() => handleSelectCategory(cat.value)}
              variant={isSelected ? "filled" : "outlined"}
              color={isSelected ? "primary" : "default"}
              sx={{
                fontWeight: 600,
                fontSize: 12,
                height: 28,
                borderRadius: 2,
                borderColor: isSelected ? "primary.main" : "divider",
              }}
            />
          );
        })}
      </Box>

      {/* 2b. Active Filter Badges Bar */}
      {filterModel.items.length > 0 && (
        <Box
          sx={{
            mb: 1.2,
            display: "flex",
            alignItems: "center",
            gap: 0.8,
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "text.secondary",
              whiteSpace: "nowrap",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Filters:
          </Typography>
          {/* Render Date Badge if any date filter is active */}
          {(() => {
            const eqItem = filterModel.items.find(
              (i) => i.field === "expense_time" && i.operator === "="
            );
            const gteItem = filterModel.items.find(
              (i) => i.field === "expense_time" && i.operator === ">="
            );
            const lteItem = filterModel.items.find(
              (i) => i.field === "expense_time" && i.operator === "<="
            );

            if (!eqItem && !gteItem && !lteItem) return null;

            let dateLabel = "";
            if (eqItem) {
              dateLabel = `📅 Only ${formatDateBadge(eqItem.value)}`;
            } else if (gteItem && lteItem) {
              dateLabel =
                gteItem.value === lteItem.value
                  ? `📅 Only ${formatDateBadge(gteItem.value)}`
                  : `📅 ${formatDateBadge(gteItem.value)} - ${formatDateBadge(lteItem.value)}`;
            } else if (gteItem) {
              dateLabel = `📅 From ${formatDateBadge(gteItem.value)}`;
            } else if (lteItem) {
              dateLabel = `📅 Up to ${formatDateBadge(lteItem.value)}`;
            }

            return (
              <Chip
                key="date_range"
                size="small"
                label={dateLabel}
                onDelete={() => handleRemoveSingleFilter("expense_time")}
                color="primary"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  fontSize: 11,
                  height: 24,
                  borderRadius: 1.5,
                  bgcolor: isDark
                    ? "rgba(99, 102, 241, 0.12)"
                    : "rgba(79, 70, 229, 0.06)",
                }}
              />
            );
          })()}

          {/* Render other non-date chips */}
          {filterModel.items
            .filter((item) => item.field !== "expense_time")
            .map((item) => {
              let label = `${item.field}: ${item.value}`;
              if (item.field === "title") label = `Title: "${item.value}"`;
              else if (item.field === "paid_by") label = `Paid by: "${item.value}"`;
              else if (item.field === "brand") label = `Brand: "${item.value}"`;
              else if (item.field === "expense_detail") label = `Notes: "${item.value}"`;
              else if (item.field === "category") label = `Cat: ${getCategoryMeta(item.value).label}`;

              return (
                <Chip
                  key={item.field}
                  size="small"
                  label={label}
                  onDelete={() => handleRemoveSingleFilter(item.field)}
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    height: 24,
                    borderRadius: 1.5,
                    bgcolor: isDark
                      ? "rgba(99, 102, 241, 0.12)"
                      : "rgba(79, 70, 229, 0.06)",
                  }}
                />
              );
            })}
          <Button
            size="small"
            onClick={handleClearAllFilters}
            sx={{
              minWidth: "auto",
              p: "2px 6px",
              fontSize: 11,
              textTransform: "none",
              color: "error.main",
              whiteSpace: "nowrap",
            }}
          >
            Clear all
          </Button>
        </Box>
      )}

      {/* 3. Header Summary Strip (Total & Open All) */}
      <Box
        sx={{
          px: 1.5,
          py: 0.8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 1.2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ color: "text.primary", fontSize: "0.85rem" }}
          >
            {rowCount} Expenses
          </Typography>
          <Chip
            size="small"
            label={`Total: ₹${parseFloat(filterdTotal || 0).toLocaleString("en-IN")}`}
            sx={{
              height: 22,
              fontWeight: 700,
              fontSize: 11,
              bgcolor: isDark ? "rgba(34, 197, 94, 0.16)" : "#e8f5e9",
              color: isDark ? "#4ade80" : "success.dark",
            }}
          />
        </Box>

        {data.length > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={handleToggleAll}
            startIcon={
              isAllExpanded ? (
                <UnfoldLessIcon sx={{ fontSize: "14px !important" }} />
              ) : (
                <UnfoldMoreIcon sx={{ fontSize: "14px !important" }} />
              )
            }
            sx={{
              textTransform: "none",
              fontSize: 11,
              fontWeight: 600,
              color: "primary.main",
              py: 0.2,
              px: 0.8,
              minWidth: 0,
              borderRadius: 1.5,
              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
            }}
          >
            {isAllExpanded ? "Collapse All" : "Open All"}
          </Button>
        )}
      </Box>

      {/* 4. Transactions Card Feed */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Skeleton
              key={n}
              variant="rounded"
              height={64}
              sx={{ borderRadius: 2.5 }}
            />
          ))}
        </Box>
      ) : data.length === 0 ? (
        <Box>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 3,
              border: "1px dashed",
              borderColor: "divider",
              bgcolor: isDark ? "rgba(30, 41, 59, 0.3)" : "rgba(0, 0, 0, 0.01)",
            }}
          >
            <ReceiptLongIcon
              sx={{ fontSize: 48, color: "text.secondary", opacity: 0.35, mb: 1 }}
            />
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              No home expenses found
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
              {activeFiltersCount > 0 || searchQuery
                ? "Try clearing your search or filters"
                : "Tap the + button below to add your first home expense"}
            </Typography>
            {(activeFiltersCount > 0 || searchQuery) && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearAllFilters}
                sx={{ mt: 1.5, textTransform: "none", fontSize: 12, borderRadius: 2 }}
              >
                Clear Filters
              </Button>
            )}
          </Paper>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {data.map((row) => {
            const isExpanded = !!expandedMap[row.id];
            const meta = getCategoryMeta(row.category);
            const total = parseFloat(row.total || row.price * row.quantity || 0);
            const billCount = parseInt(row.bill_count) || 0;
            const isOwner = Boolean(
              (session?.id && String(session.id) === String(row.created_by)) ||
              (session?.firstname && row.paid_by && row.paid_by.toLowerCase().includes(session.firstname.toLowerCase()))
            );

            // Format date directly as: "11 Jan 26" or "12 Jun 28"
            let formattedDate = "";
            let fullFormattedDate = "";
            if (row.expense_time) {
              try {
                const d = new Date(row.expense_time);
                const day = d.getDate();
                const months = [
                  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
                ];
                const month = months[d.getMonth()];
                const yr = String(d.getFullYear()).slice(-2);
                formattedDate = `${day} ${month} ${yr}`;
                fullFormattedDate = `${day} ${month} ${d.getFullYear()}`;
              } catch (e) {
                formattedDate = row.expense_time;
                fullFormattedDate = row.expense_time;
              }
            }

            return (
              <Paper
                key={row.id}
                elevation={0}
                sx={{
                  position: "relative",
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: isOwner
                    ? (isDark ? "rgba(239, 68, 68, 0.3)" : "rgba(239, 68, 68, 0.2)")
                    : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"),
                  borderLeft: `4px solid ${meta.color}`,
                  background: isOwner
                    ? (isDark
                        ? "linear-gradient(45deg, rgba(239, 68, 68, 0.3) 0px, rgba(239, 68, 68, 0.3) 15px, rgba(30, 41, 59, 0.55) 15px)"
                        : "linear-gradient(45deg, rgba(239, 68, 68, 0.2) 0px, rgba(239, 68, 68, 0.2) 15px, #ffffff 15px)")
                    : (isDark ? "rgba(30, 41, 59, 0.55)" : "#ffffff"),
                  overflow: "hidden",
                  transition: "background 0.15s ease, border-color 0.15s ease",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(30, 41, 59, 0.85)" : "rgba(0, 0, 0, 0.015)",
                  },
                }}
              >
                {/* 15px 45-degree red accent strip in top corner for owner */}
                {isOwner && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 0,
                      height: 0,
                      borderStyle: "solid",
                      borderWidth: "0 15px 15px 0",
                      borderColor: "transparent #ef4444 transparent transparent",
                      zIndex: 1,
                    }}
                  />
                )}
                {/* Compact Card Row */}
                <Box
                  onClick={() => toggleExpand(row.id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleExpand(row.id);
                    }
                  }}
                  sx={{
                    px: 1.5,
                    py: 1.2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    gap: 1.2,
                  }}
                >
                  {/* Left: Category Icon Avatar */}
                  <Avatar
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: meta.bg,
                      color: meta.color,
                      flexShrink: 0,
                    }}
                  >
                    {meta.icon}
                  </Avatar>

                  {/* Middle: Title & Meta Info */}
                  <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        color: "text.primary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: "0.92rem",
                        lineHeight: 1.3,
                      }}
                    >
                      {row.title || `Expense #${row.id}`}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.3,
                        fontSize: "0.74rem",
                        mt: 0.2,
                      }}
                    >
                      {meta.label}
                      {row.paid_by ? ` • Paid by ${row.paid_by}` : ""}
                    </Typography>
                  </Box>

                  {/* Right: Amount & Bill Attachment Pill */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      textAlign: "right",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 0.3,
                    }}
                  >
                    {/* Line 1: Amount + Chevron */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                      <Typography
                        variant="body2"
                        fontWeight={800}
                        sx={{
                          color: "text.primary",
                          fontSize: "0.96rem",
                          lineHeight: 1,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: "error.main",
                            fontWeight: 700,
                            mr: 0.15,
                          }}
                        >
                          ₹
                        </Box>
                        {total.toLocaleString("en-IN")}
                      </Typography>

                      <IconButton
                        size="small"
                        sx={{
                          p: 0.1,
                          ml: 0.2,
                          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>

                    {/* Line 2: Date + (Bill Receipt Pill or Specs) */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      {formattedDate && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                            lineHeight: 1,
                          }}
                        >
                          {formattedDate}
                        </Typography>
                      )}

                      {billCount > 0 ? (
                        <Chip
                          size="small"
                          icon={
                            <DescriptionIcon
                              sx={{ fontSize: "11px !important", color: "primary.main" }}
                            />
                          }
                          label={`${billCount} Bill`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenBillModal) onOpenBillModal(row);
                          }}
                          sx={{
                            height: 18,
                            fontSize: 9.5,
                            fontWeight: 700,
                            bgcolor: isDark
                              ? "rgba(99, 102, 241, 0.18)"
                              : "rgba(79, 70, 229, 0.08)",
                            color: "primary.main",
                            cursor: "pointer",
                            "&:hover": { filter: "brightness(0.95)" },
                          }}
                        />
                      ) : (
                        row.quantity > 1 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem", lineHeight: 1 }}
                          >
                            {row.quantity} items
                          </Typography>
                        )
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Expanded Details Drawer (Revealed on Tap) */}
                <Collapse in={isExpanded} timeout="auto">
                  <Divider />
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: isDark ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
                    }}
                  >
                    {/* Item Details Grid */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1 }}>
                      {fullFormattedDate && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            DATE
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>
                            {fullFormattedDate}
                          </Typography>
                        </Box>
                      )}
                      {row.brand && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            BRAND
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>
                            {row.brand}
                          </Typography>
                        </Box>
                      )}
                      {row.weight && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            WEIGHT / SIZE
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>
                            {row.weight}
                          </Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                          PRICING
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>
                          {row.quantity} x ₹{parseFloat(row.price || 0).toFixed(2)} = ₹{total.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    {row.expense_detail && (
                      <Box sx={{ mb: 1.2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                          NOTES & DETAILS
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 12, color: "text.primary" }}>
                          {row.expense_detail}
                        </Typography>
                      </Box>
                    )}

                    {/* Actions Bar */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pt: 1,
                        borderTop: "1px dashed",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        ID #{row.id} {row.paid_by && `• Added by ${row.paid_by}`}
                      </Typography>

                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        {billCount > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DescriptionIcon sx={{ fontSize: "14px !important" }} />}
                            onClick={() => onOpenBillModal && onOpenBillModal(row)}
                            sx={{
                              height: 26,
                              fontSize: 11,
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 1.5,
                            }}
                          >
                            View Bill ({billCount})
                          </Button>
                        )}

                        {isOwner && onDeleteExpense && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDeleteExpense(row)}
                            sx={{ p: 0.4 }}
                            title="Delete Expense"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* 5. Mobile Pagination */}
      {rowCount > paginationModel.pageSize && (
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={rowCount}
          rowsPerPage={paginationModel.pageSize}
          page={paginationModel.page}
          onPageChange={(e, newPage) =>
            setPaginationModel((prev) => ({ ...prev, page: newPage }))
          }
          onRowsPerPageChange={(e) =>
            setPaginationModel((prev) => ({
              ...prev,
              pageSize: parseInt(e.target.value, 10),
              page: 0,
            }))
          }
          sx={{ mt: 1, px: 1 }}
        />
      )}

      {/* 6. Advanced Column-Based Filter Modal */}
      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TuneIcon color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Advanced Filters
            </Typography>
            {localActiveCount > 0 && (
              <Chip
                size="small"
                label={`${localActiveCount} set`}
                color="primary"
                sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
              />
            )}
          </Box>
          <IconButton size="small" onClick={() => setFilterModalOpen(false)}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "16px !important",
            pb: 2,
            overflowY: "auto",
          }}
        >
          {/* Column Search Group */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Search by Columns
            </Typography>

            {/* Title / Item Name */}
            <TextField
              fullWidth
              size="small"
              label="Item / Title"
              placeholder="e.g. Oil, Milk, Bread, Petrol"
              value={localFilters.title}
              onChange={(e) =>
                setLocalFilters((prev) => ({ ...prev, title: e.target.value }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "primary.main", fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: localFilters.title ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setLocalFilters((prev) => ({ ...prev, title: "" }))
                      }
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            {/* Paid By / Added By */}
            <TextField
              fullWidth
              size="small"
              label="Paid / Added By"
              placeholder="e.g. Deva, Krishna, Rahul"
              value={localFilters.paid_by}
              onChange={(e) =>
                setLocalFilters((prev) => ({ ...prev, paid_by: e.target.value }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "primary.main", fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: localFilters.paid_by ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setLocalFilters((prev) => ({ ...prev, paid_by: "" }))
                      }
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            {/* Brand / Store */}
            <TextField
              fullWidth
              size="small"
              label="Brand / Store"
              placeholder="e.g. Fortune, Amul, DMart, Amazon"
              value={localFilters.brand}
              onChange={(e) =>
                setLocalFilters((prev) => ({ ...prev, brand: e.target.value }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StoreIcon sx={{ color: "primary.main", fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: localFilters.brand ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setLocalFilters((prev) => ({ ...prev, brand: "" }))
                      }
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            {/* Notes / Detail */}
            <TextField
              fullWidth
              size="small"
              label="Description / Notes"
              placeholder="e.g. Monthly ration, Dinner, Online"
              value={localFilters.expense_detail}
              onChange={(e) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  expense_detail: e.target.value,
                }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon
                      sx={{ color: "primary.main", fontSize: 18 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: localFilters.expense_detail ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setLocalFilters((prev) => ({
                          ...prev,
                          expense_detail: "",
                        }))
                      }
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Box>

          <Divider sx={{ my: 0.5 }} />

          {/* Date Range Section */}
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.2,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Date Range
              </Typography>
              {(localFilters.startDate || localFilters.endDate) && (
                <Typography
                  variant="caption"
                  onClick={() => handleSetDatePreset("clear")}
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 11,
                  }}
                >
                  Clear dates
                </Typography>
              )}
            </Box>

            {/* Quick Date Presets */}
            <Box sx={{ display: "flex", gap: 0.8, mb: 1.5, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label="Today"
                onClick={() => handleSetDatePreset("today")}
                variant="outlined"
                sx={{ borderRadius: 2, fontSize: 11, fontWeight: 600, height: 26 }}
              />
              <Chip
                size="small"
                label="Yesterday"
                onClick={() => handleSetDatePreset("yesterday")}
                variant="outlined"
                sx={{ borderRadius: 2, fontSize: 11, fontWeight: 600, height: 26 }}
              />
              <Chip
                size="small"
                label="This Month"
                onClick={() => handleSetDatePreset("thisMonth")}
                variant="outlined"
                sx={{ borderRadius: 2, fontSize: 11, fontWeight: 600, height: 26 }}
              />
            </Box>

            {/* From Date & To Date Inputs */}
            <Box sx={{ display: "flex", gap: 1.2 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={localFilters.startDate}
                onChange={(e) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={localFilters.endDate}
                onChange={(e) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    endDate: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            {/* If both dates are same */}
            {localFilters.startDate &&
              localFilters.startDate === localFilters.endDate && (
                <Box
                  sx={{
                    mt: 1,
                    p: 0.8,
                    bgcolor: isDark
                      ? "rgba(99, 102, 241, 0.12)"
                      : "rgba(79, 70, 229, 0.08)",
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                  }}
                >
                  <CalendarTodayIcon
                    color="primary"
                    sx={{ fontSize: "14px !important" }}
                  />
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ fontWeight: 600, fontSize: 11 }}
                  >
                    Showing expenses for only this day ({formatDateBadge(localFilters.startDate)})
                  </Typography>
                </Box>
              )}
          </Box>

          <Divider sx={{ my: 0.5 }} />

          {/* Category Filter */}
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{
                display: "block",
                mb: 1.2,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Category
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
              <Chip
                size="small"
                label="All Categories"
                onClick={() =>
                  setLocalFilters((prev) => ({ ...prev, category: "" }))
                }
                variant={localFilters.category === "" ? "filled" : "outlined"}
                color={localFilters.category === "" ? "primary" : "default"}
                sx={{ borderRadius: 2, fontWeight: 600, fontSize: 12, height: 28 }}
              />
              {categoryOptions.map((cat) => {
                const isSelected = localFilters.category === cat.value;
                const meta = getCategoryMeta(cat.value);
                return (
                  <Chip
                    key={cat.value}
                    size="small"
                    icon={React.cloneElement(meta.icon, {
                      sx: {
                        color: isSelected ? "inherit" : meta.color,
                        fontSize: "16px !important",
                      },
                    })}
                    label={meta.label}
                    onClick={() =>
                      setLocalFilters((prev) => ({
                        ...prev,
                        category: isSelected ? "" : cat.value,
                      }))
                    }
                    variant={isSelected ? "filled" : "outlined"}
                    color={isSelected ? "primary" : "default"}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: 12,
                      height: 28,
                      borderColor: isSelected ? "primary.main" : "divider",
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          <Divider sx={{ my: 0.5 }} />

          {/* Smart Query Syntax Box */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
              <CodeIcon sx={{ fontSize: 18, color: "primary.main" }} />
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Smart Query Syntax
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1, fontSize: 11 }}
            >
              You can also type direct smart queries in the search bar in any order (plain words search title; addedby supports partial names like "jai"):
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
              {[
                "addedby:deva date:26-aug-2026 title:milk",
                "addedby:jai date:today title:oil",
                "oil addedby:deva",
                "brand:fortune title:oil",
              ].map((queryExample) => (
                <Chip
                  key={queryExample}
                  size="small"
                  label={queryExample}
                  onClick={() => {
                    if (onSearchChange) {
                      onSearchChange({ target: { value: queryExample } });
                    }
                    setFilterModalOpen(false);
                  }}
                  clickable
                  variant="outlined"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 11,
                    height: 26,
                    justifyContent: "flex-start",
                    borderRadius: 1.5,
                    bgcolor: isDark ? "rgba(99, 102, 241, 0.08)" : "#ffffff",
                    borderColor: "divider",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: isDark
                        ? "rgba(99, 102, 241, 0.16)"
                        : "rgba(79, 70, 229, 0.06)",
                    },
                  }}
                />
              ))}
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            pt: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={handleResetModalFilters}
            color="inherit"
            disabled={localActiveCount === 0}
            sx={{ textTransform: "none", fontSize: 13, color: "text.secondary" }}
          >
            Reset
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => setFilterModalOpen(false)}
              color="inherit"
              sx={{ textTransform: "none", fontSize: 13 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyModalFilters}
              sx={{
                textTransform: "none",
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 2,
                px: 2.5,
              }}
            >
              {localActiveCount > 0
                ? `Apply Filters (${localActiveCount})`
                : "Apply Filters"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export { ExpenseMobileView as HomeMobileView };
export default ExpenseMobileView;
